import {
  RequirementType,
  GetMissingFieldsLevelParameter,
  VerificationLevel
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import {
  PATH_ONBOARDING_PAYEE,
  PATH_ONBOARDING_PAYEE_REQUIREMENT_DETAILS
} from "..";
import { useSession } from "@hooks/useSession";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { queriesOnboarding, queriesRequirements } from "@query";
import { useModalExit } from "../components/ModalExit";
import { OnboardingFlow } from "../components/OnboardingFlow";
import { RouteOnboardingRequirementDetails } from "../steps/EligibilityRequirements/RouteOnboardingRequirementDetails";
import { OnboardingModule } from "../types";

export const RoutePayeeOnboarding: React.FC = () => {
  const history = useHistory();
  const { accountId } = useSession();
  const queryFeatureFlags = useQueryFeatureFlags();
  const queryVerifications = queriesOnboarding.getOnboardingStatus.useQuery(
    undefined,
    { select: status => status.verifications }
  );
  // TODO(v3-blocker): v1 also filtered on `payerPayeeEngagementIds != null`;
  // v3 `listPayeeRequirements` has no existence operator, so that constraint
  // is dropped.
  const queryRequirements =
    queriesRequirements.listPayeeRequirements.useQueryAll(
      {
        filter: {
          payeeAccountId: { in: [accountId] }
        }
      },
      { enabled: !!accountId }
    );

  const requirements = queryRequirements.flatData || [];

  const hasPayoutMethodRequirement = requirements.some(
    requirement => requirement.type === RequirementType.PAYOUT_METHOD
  );

  const hasNonGeneralRequirements = requirements.some(
    requirement =>
      requirement.type !== RequirementType.REGISTRATION &&
      requirement.type !== RequirementType.TAX_VERIFICATION &&
      requirement.type !== RequirementType.PAYOUT_METHOD
  );

  const modules = useMemo((): OnboardingModule[] => {
    const payeeOnboardingModules: OnboardingModule[] = [];

    payeeOnboardingModules.push({
      type: "create_account",
      options: {
        accountTypes: ["Business", "Individual"],
        verificationLevel: GetMissingFieldsLevelParameter.TAX,
        show1099Message: true,
        allowUSInternationalPerson:
          queryFeatureFlags.data?.internationalUsPerson
      }
    });

    payeeOnboardingModules.push({
      type: "certify_tax_payer_info"
    });

    if (hasPayoutMethodRequirement) {
      payeeOnboardingModules.push({
        type: "payout_method"
      });
    }

    if (hasNonGeneralRequirements) {
      payeeOnboardingModules.push({
        type: "eligibility_requirements"
      });
    }

    if (queryFeatureFlags.data?.taxWithholdingOnboarding) {
      payeeOnboardingModules.push({ type: "tax_withholdings" });
    }

    return payeeOnboardingModules;
  }, [
    hasNonGeneralRequirements,
    hasPayoutMethodRequirement,
    queryFeatureFlags.data?.taxWithholdingOnboarding,
    queryFeatureFlags.data?.internationalUsPerson
  ]);

  const modalExit = useModalExit();

  return (
    <Switch>
      {/*CUSTOM ROUTE FOR REQUIREMENTS*/}
      <Route
        path={PATH_ONBOARDING_PAYEE_REQUIREMENT_DETAILS}
        component={RouteOnboardingRequirementDetails}
      />

      <OnboardingFlow
        onBack={
          queryVerifications.data?.tax &&
          queryVerifications.data?.tax !== VerificationLevel.NONE
            ? () => {
                modalExit.open({
                  onboardingType: "Payee"
                });
              }
            : undefined
        }
        basePath={PATH_ONBOARDING_PAYEE}
        modules={modules}
        onSuccess={() => {
          history.push("/member/dashboard");
        }}
      />
    </Switch>
  );
};
